<template>
  <section class="mosaico-tv" aria-label="Agenda do dia das salas de reunião">
    <div class="mosaico-tv-header" role="banner">
      <div class="mosaico-tv-lead">
        <img class="mosaico-tv-logo" :src="logoRoboflex" alt="Roboflex" decoding="async" />
        <span class="mosaico-tv-divisor" aria-hidden="true"></span>
        <h1 class="mosaico-tv-titulo">Salas de reunião</h1>
      </div>
      <div class="mosaico-tv-header-dir">
        <div v-if="salas.length" class="mosaico-tv-contagem" role="status">
          <span class="mosaico-tv-contagem-item mosaico-tv-contagem-item--livre">
            <strong>{{ totalLivres }}</strong> {{ totalLivres === 1 ? "livre" : "livres" }}
          </span>
          <span class="mosaico-tv-contagem-item mosaico-tv-contagem-item--ocupada">
            <strong>{{ totalOcupadas }}</strong> {{ totalOcupadas === 1 ? "ocupada" : "ocupadas" }}
          </span>
        </div>
        <time class="mosaico-tv-relogio" :datetime="agoraDatetimeISO">
          <span class="mosaico-tv-relogio-hora">{{ relogioHoraCabecalho }}</span>
          <span class="mosaico-tv-relogio-data">{{ relogioDataCabecalho }}</span>
        </time>
      </div>
    </div>

    <div v-if="carregando && !carregado" class="mosaico-tv-vazio" role="status">
      <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
      <span>Carregando salas…</span>
    </div>

    <div v-else-if="erro && !salas.length" class="mosaico-tv-vazio" role="alert">
      <p>{{ erro }}</p>
    </div>

    <div v-else-if="!salas.length" class="mosaico-tv-vazio">
      <p>Nenhuma sala de reunião ativa.</p>
    </div>

    <div v-else class="mosaico-tv-agenda" :style="{ '--mosaico-salas': salas.length }">
      <div class="mosaico-tv-canto" aria-hidden="true"></div>

      <header v-for="sala in salas" :key="'cab-' + sala.id" class="mosaico-tv-cab" :class="classeStatus(sala)">
        <h2 class="mosaico-tv-nome">{{ sala.nome || "Sala de reunião" }}</h2>
        <p v-if="sala.capacidade" class="mosaico-tv-capacidade">
          <i class="fas fa-users" aria-hidden="true"></i>
          capacidade {{ sala.capacidade }} {{ sala.capacidade === 1 ? "pessoa" : "pessoas" }}
        </p>
        <div class="mosaico-tv-cab-status">
          <span class="mosaico-tv-etiqueta">
            <span class="mosaico-tv-etiqueta-ponto" aria-hidden="true"></span>
            {{ sala.erro ? "Indisponível" : statusDe(sala).rotulo }}
          </span>
          <p v-if="!sala.erro && statusDe(sala).tempoValor" class="mosaico-tv-tempo">
            {{ statusDe(sala).tempoRotulo }}
            <strong>{{ statusDe(sala).tempoValor }}</strong>
          </p>
        </div>
      </header>

      <div class="mosaico-tv-horas" aria-hidden="true">
        <span v-for="m in marcasHoras" :key="m.label" class="mosaico-tv-hora-marca" :class="{
          'mosaico-tv-hora-marca--inicio': m.isPrimeira,
          'mosaico-tv-hora-marca--fim': m.isUltima,
        }" :style="{ top: m.topPct + '%' }">{{ m.label }}</span>
        <span v-if="linhaAgoraTopPct != null" class="mosaico-tv-agora-hora" :style="{ top: linhaAgoraTopPct + '%' }">
          {{ relogioHoraCabecalho }}
        </span>
      </div>

      <div class="mosaico-tv-trilhas">
        <div v-for="sala in salas" :key="'trilha-' + sala.id" class="mosaico-tv-trilha">
          <p v-if="sala.erro" class="mosaico-tv-trilha-aviso">{{ mensagemErro(sala) }}</p>
          <p v-else-if="!sala.blocos.length" class="mosaico-tv-trilha-aviso">Sem reservas hoje</p>
          <div v-for="(bl, idx) in sala.blocos" :key="idx" class="mosaico-tv-bloco" :class="[
            'mosaico-tv-bloco--' + momentoDoBloco(bl),
            {
              'mosaico-tv-bloco--compacto': bl.heightPct < 8,
              'mosaico-tv-bloco--micro': bl.heightPct < 4,
            },
          ]" :style="{
            top: bl.topPct + '%',
            height: bl.heightPct + '%',
            left: 'calc(0.3vw + (100% - 0.6vw) * ' + bl.leftPct / 100 + ')',
            width: 'calc((100% - 0.6vw) * ' + bl.widthPct / 100 + ')',
          }">
            <span class="mosaico-tv-bloco-linha">
              <span class="mosaico-tv-bloco-hora">{{ bl.horario }}</span>
              <span v-if="bl.participantes" class="mosaico-tv-bloco-pessoas"
                :title="bl.participantes === 1 ? '1 pessoa' : bl.participantes + ' pessoas'">
                <i class="fas fa-user" aria-hidden="true"></i>{{ bl.participantes }}
              </span>
            </span>
            <span class="mosaico-tv-bloco-org">{{ bl.organizador || "Reservado" }}</span>
          </div>
        </div>

        <div v-if="linhaAgoraTopPct != null" class="mosaico-tv-agora-linha" :style="{ top: linhaAgoraTopPct + '%' }"
          aria-hidden="true"></div>
      </div>

      <div class="mosaico-tv-canto" aria-hidden="true"></div>
      <footer v-for="sala in salas" :key="'qr-' + sala.id" class="mosaico-tv-rodape">
        <img v-if="sala.qrCodeDataUrl" class="mosaico-tv-qr" :src="sala.qrCodeDataUrl"
          alt="QR Code para agendar nesta sala pelo Google Agenda" />
      </footer>
    </div>
  </section>
</template>

<script>
import { eventosMosaicoPublico } from "@/services/sala-reuniao-service";
import {
  DISPLAY_END_MIN,
  DISPLAY_START_MIN,
  blocosDoDiaGrade,
  estadoMosaicoSala,
  linkCriarEventoGoogleCalendar,
  marcasHorasParaGrade,
  minutosDesdeMeiaNoite,
} from "@/utils/sala-agenda-helpers";
import { gerarQrCodeDataUrl } from "@/utils/qrcode-helpers";
import logoRoboflex from "@/assets/img/logo-roboflex-clara.png";

/**
 * Intervalo curto de propósito: é o atraso máximo entre criar/editar um evento e a TV mostrar
 * a sala ocupada. Como é só leitura pública (sem JWT), o custo de poll mais frequente é baixo.
 */
const POLL_MS = 15_000;
const RELOGIO_MS = 15_000;

export default {
  name: "MosaicoSalasView",

  data() {
    return {
      logoRoboflex,
      salas: [],
      carregando: false,
      carregado: false,
      erro: "",
      pollTimer: null,
      relogioTimer: null,
      agora: new Date(),
    };
  },

  computed: {
    marcasHoras() {
      return marcasHorasParaGrade();
    },
    /** Posição da linha "agora" na faixa 8:00–18:00 (%); some fora desse horário. */
    linhaAgoraTopPct() {
      const min = minutosDesdeMeiaNoite(this.agora);
      if (min < DISPLAY_START_MIN || min > DISPLAY_END_MIN) return null;
      return ((min - DISPLAY_START_MIN) / (DISPLAY_END_MIN - DISPLAY_START_MIN)) * 100;
    },
    totalOcupadas() {
      return this.salas.filter((s) => !s.erro && this.statusDe(s).codigo === "ocupada").length;
    },
    totalLivres() {
      return this.salas.filter((s) => !s.erro && this.statusDe(s).codigo === "livre").length;
    },
    relogioDataCabecalho() {
      return this.agora.toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
      });
    },
    relogioHoraCabecalho() {
      return this.agora.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    agoraDatetimeISO() {
      return this.agora.toISOString();
    },
  },

  mounted() {
    document.documentElement.classList.add("sala-porta-html");
    this.iniciarRelogio();
    this.carregar();
    this.pollTimer = setInterval(() => this.carregar({ silencioso: true }), POLL_MS);
    // Guarda contra o navegador atrasar o setInterval em aba fora de foco/minimizada
    // (ex.: alguém trocou de janela): ao voltar, busca na hora em vez de esperar o próximo tick.
    document.addEventListener("visibilitychange", this.onVisibilidadeMudou);
    window.addEventListener("focus", this.atualizarAgora);
  },

  beforeUnmount() {
    this.pararRelogio();
    if (this.pollTimer) clearInterval(this.pollTimer);
    document.removeEventListener("visibilitychange", this.onVisibilidadeMudou);
    window.removeEventListener("focus", this.atualizarAgora);
    document.documentElement.classList.remove("sala-porta-html");
  },

  methods: {
    statusDe(sala) {
      return estadoMosaicoSala(sala.eventos, this.agora);
    },
    classeStatus(sala) {
      if (sala.erro) return "mosaico-tv-cab--indisponivel";
      return "mosaico-tv-cab--" + this.statusDe(sala).codigo;
    },
    /** passado | agora | futuro — o bloco em andamento ganha destaque, os já encerrados ficam apagados. */
    momentoDoBloco(bl) {
      const agora = this.agora.getTime();
      const ini = Date.parse(bl.detalhes?.inicio);
      const fim = Date.parse(bl.detalhes?.fim);
      if (Number.isFinite(fim) && fim <= agora) return "passado";
      if (Number.isFinite(ini) && ini <= agora) return "agora";
      return "futuro";
    },
    onVisibilidadeMudou() {
      if (document.visibilityState === "visible") this.atualizarAgora();
    },
    atualizarAgora() {
      this.agora = new Date();
      this.atualizarBlocos();
      this.carregar({ silencioso: true });
    },
    mensagemErro(sala) {
      if (sala.erro === "google_not_connected") return "Google Agenda não conectado.";
      if (sala.erro === "usuario_servico_ausente") return "Conta da sala não encontrada.";
      return "Não foi possível ler a agenda.";
    },

    async carregar(opts = {}) {
      const silencioso = Boolean(opts.silencioso);
      if (!silencioso) this.carregando = true;
      try {
        const data = await eventosMosaicoPublico();
        await this.aplicarSalas(Array.isArray(data?.salas) ? data.salas : []);
        this.erro = "";
        this.carregado = true;
      } catch (e) {
        if (!this.carregado) {
          const status = e?.response?.status;
          this.erro =
            status === 404
              ? "O painel das salas ainda não está publicado neste servidor."
              : e?.response?.data?.message || e?.message || "Não foi possível carregar as salas.";
        }
      } finally {
        this.carregando = false;
      }
    },

    async aplicarSalas(lista) {
      const anteriores = new Map(this.salas.map((s) => [s.id, s]));
      const salas = lista.map((item) => {
        const id = item?.id;
        const anterior = anteriores.get(id);
        const googleCalendarId = item?.google_calendar_id || "";
        const nome = item?.nome || "Sala de reunião";
        const eventos = Array.isArray(item?.events) ? item.events : [];
        const mesmoQr =
          anterior &&
          anterior.googleCalendarId === googleCalendarId &&
          anterior.nome === nome &&
          anterior.qrCodeDataUrl;
        const capacidade = Number(item?.capacidade);
        return {
          id,
          nome,
          capacidade: Number.isFinite(capacidade) && capacidade > 0 ? capacidade : null,
          googleCalendarId,
          eventos,
          blocos: blocosDoDiaGrade(eventos, this.agora),
          erro: item?.erro || "",
          qrCodeDataUrl: mesmoQr ? anterior.qrCodeDataUrl : "",
        };
      });

      await Promise.all(
        salas.map(async (sala) => {
          if (sala.qrCodeDataUrl || !sala.googleCalendarId) return;
          const link = linkCriarEventoGoogleCalendar(sala.googleCalendarId, sala.nome);
          sala.qrCodeDataUrl = await gerarQrCodeDataUrl(link, {
            width: 320,
            margin: 1,
            corEscura: "#000000",
            corClara: "#FDFDFD",
          });
        }),
      );

      this.salas = salas;
    },

    /** Recalcula os blocos quando o dia vira sem novo poll (ex.: TV ligada de madrugada). */
    atualizarBlocos() {
      this.salas.forEach((sala) => {
        sala.blocos = blocosDoDiaGrade(sala.eventos, this.agora);
      });
    },

    iniciarRelogio() {
      this.agora = new Date();
      this.relogioTimer = setInterval(() => {
        const diaAnterior = this.agora.getDate();
        this.agora = new Date();
        if (this.agora.getDate() !== diaAnterior) this.atualizarBlocos();
      }, RELOGIO_MS);
    },
    pararRelogio() {
      if (this.relogioTimer != null) {
        clearInterval(this.relogioTimer);
        this.relogioTimer = null;
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Roboto:wght@400;700&display=swap");

/* Paleta do Manual de Identidade Visual (Roboflex) */
.mosaico-tv {
  --mosaico-azul-profundo: #052057;
  --mosaico-azul-escuro: #102958;
  --mosaico-azul-medio: #143266;
  --mosaico-azul-apoio: #0c5389;
  --mosaico-azul: #3779e3;
  --mosaico-ciano: #00ccff;
  --mosaico-branco: #fdfdfd;
  --mosaico-cinza: #919191;
  --mosaico-preto: #000000;
  --mosaico-livre: #3fb27f;
  --mosaico-ocupada: #e5534b;
  --mosaico-indisponivel: #919191;
  --mosaico-agora: #ff3b30;
  --mosaico-texto-suave: #b8c4d9;
  --mosaico-gap: 0.6vw;

  position: fixed;
  inset: 0;
  z-index: 30;
  box-sizing: border-box;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1.6vh;
  padding: 2.2vh 1.6vw 2vh;
  font-family: "Montserrat", "Roboto", Arial, sans-serif;
  color: var(--mosaico-branco);
  background: var(--mosaico-azul-profundo);
}

/* ---------- Cabeçalho ---------- */
.mosaico-tv-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2vw;
  margin: 0;
  padding: 0 0 1.4vh;
  background: transparent;
  box-shadow: none;
  border-bottom: 1px solid var(--mosaico-azul-medio);
  color: var(--mosaico-branco);
}

.mosaico-tv-lead {
  display: flex;
  align-items: center;
  gap: 1.6vw;
  min-width: 0;
}

/* Arejamento do manual: área livre de metade da altura do símbolo em volta da marca */
.mosaico-tv-logo {
  height: 5.6vh;
  width: auto;
  flex-shrink: 0;
  padding: 1.4vh 0;
}

.mosaico-tv-divisor {
  width: 0.2vw;
  min-width: 2px;
  height: 5vh;
  background: var(--mosaico-cinza);
}

.mosaico-tv-titulo {
  margin: 0;
  font-size: clamp(1.1rem, 2.4vh, 1.9rem);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--mosaico-texto-suave);
}

.mosaico-tv-header-dir {
  display: flex;
  align-items: center;
  gap: 2.4vw;
}

.mosaico-tv-contagem {
  display: flex;
  gap: 1.4vw;
  font-size: clamp(0.95rem, 2vh, 1.5rem);
  font-weight: 600;
  color: var(--mosaico-texto-suave);
}

.mosaico-tv-contagem-item {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35em;
  padding-left: 0.6em;
  border-left: 0.35vw solid var(--status);
}

.mosaico-tv-contagem-item strong {
  font-size: 1.35em;
  font-weight: 800;
  color: var(--status);
  font-variant-numeric: tabular-nums;
}

.mosaico-tv-contagem-item--livre {
  --status: var(--mosaico-livre);
}

.mosaico-tv-contagem-item--ocupada {
  --status: var(--mosaico-ocupada);
}

.mosaico-tv-relogio {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.05;
  font-variant-numeric: tabular-nums lining-nums;
}

.mosaico-tv-relogio-hora {
  font-size: clamp(1.8rem, 4.4vh, 3.2rem);
  font-weight: 700;
}

.mosaico-tv-relogio-data {
  font-size: clamp(0.95rem, 1.9vh, 1.4rem);
  font-weight: 600;
  text-transform: capitalize;
  color: var(--mosaico-cinza);
}

.mosaico-tv-vazio {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5vh;
  font-size: clamp(1.4rem, 3vh, 2.2rem);
  font-weight: 700;
  text-align: center;
  color: var(--mosaico-texto-suave);
}

.mosaico-tv-vazio p {
  margin: 0;
}

/* ---------- Agenda: coluna de horas + uma coluna por sala ---------- */
.mosaico-tv-agenda {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 4.2vw repeat(var(--mosaico-salas, 1), minmax(0, 1fr));
  grid-template-rows: auto minmax(0, 1fr) auto;
  column-gap: var(--mosaico-gap);
  row-gap: 1.2vh;
}

.mosaico-tv-cab {
  container-type: inline-size;
  --status: var(--mosaico-indisponivel);
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.8vh;
  box-sizing: border-box;
  padding: 1.4vh 0.7vw 1.4vh 0.9vw;
  border-radius: 0.8vh;
  border: 1px solid #3a4f73;
  border-top: 0.7vh solid var(--status);
  background: #21375f;
}

/*
 * Cores já misturadas, sem color-mix(): com var() dentro, o navegador de TV sem suporte
 * aceita a regra e só a descarta ao pintar, e o fundo fica transparente.
 */
.mosaico-tv-cab--livre {
  --status: var(--mosaico-livre);
  --status-sombra: rgba(63, 178, 127, 0.55);
  background: #163b5d;
  border-color: #21586e;
  border-top-color: var(--status);
}

.mosaico-tv-cab--ocupada {
  --status: var(--mosaico-ocupada);
  --status-sombra: rgba(229, 83, 75, 0.55);
  background: #2c2e56;
  border-color: #533c5e;
  border-top-color: var(--status);
}

.mosaico-tv-cab--indisponivel {
  --status: var(--mosaico-indisponivel);
  --status-sombra: rgba(145, 145, 145, 0.55);
}

.mosaico-tv-nome {
  max-width: 100%;
  margin: 0;
  font-size: clamp(0.95rem, 9cqw, 2rem);
  font-weight: 700;
  line-height: 1.12;
  color: var(--mosaico-branco);
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mosaico-tv-capacidade {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.45em;
  font-size: clamp(0.65rem, 5.4cqw, 1.05rem);
  font-weight: 600;
  color: var(--mosaico-texto-suave);
  white-space: nowrap;
}

.mosaico-tv-capacidade i {
  color: var(--mosaico-ciano);
}

.mosaico-tv-cab-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6vh 4cqw;
}

.mosaico-tv-etiqueta {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.45em;
  padding: 0.35em 0.8em;
  border-radius: 999px;
  font-size: clamp(0.65rem, 5cqw, 1rem);
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--mosaico-azul-profundo);
  background: var(--status);
  box-shadow: 0 2px 10px var(--status-sombra, rgba(0, 0, 0, 0.25));
}

.mosaico-tv-etiqueta-ponto {
  width: 0.5em;
  height: 0.5em;
  border-radius: 50%;
  background: var(--mosaico-azul-profundo);
  opacity: 0.55;
}

.mosaico-tv-tempo {
  margin: 0;
  font-family: "Roboto", "Montserrat", Arial, sans-serif;
  font-size: clamp(0.65rem, 5.2cqw, 1.05rem);
  line-height: 1.25;
  color: var(--mosaico-texto-suave);
}

.mosaico-tv-tempo strong {
  color: var(--mosaico-branco);
  font-weight: 700;
}

/* QR no pé da coluna. 11vh numa TV de 42" fica perto de 6 cm: menor que antes, ainda legível de perto. */
.mosaico-tv-rodape {
  container-type: inline-size;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8vw;
}

.mosaico-tv-qr {
  flex: 0 0 auto;
  width: min(52cqw, 11vh);
  height: auto;
  aspect-ratio: 1;
  padding: 0.3vh;
  border-radius: 0.4vh;
  background: var(--mosaico-branco);
}

/* Legenda de horas — mesma escala linear dos blocos (8:00–18:00) */
.mosaico-tv-canto {
  min-width: 0;
}

.mosaico-tv-horas {
  position: relative;
  min-height: 0;
}

.mosaico-tv-hora-marca {
  position: absolute;
  right: 0.5vw;
  transform: translateY(-50%);
  font-size: clamp(0.7rem, 1.5vh, 1.05rem);
  font-weight: 600;
  color: var(--mosaico-cinza);
  font-variant-numeric: tabular-nums;
}

.mosaico-tv-hora-marca--inicio {
  transform: translateY(0);
}

.mosaico-tv-hora-marca--fim {
  transform: translateY(-100%);
}

.mosaico-tv-agora-hora {
  position: absolute;
  right: 0;
  z-index: 2;
  transform: translateY(-50%);
  padding: 0.25em 0.45em;
  border-radius: 0.4em;
  font-size: clamp(0.72rem, 1.6vh, 1.1rem);
  font-weight: 800;
  color: var(--mosaico-branco);
  background: var(--mosaico-agora);
  font-variant-numeric: tabular-nums;
}

.mosaico-tv-trilhas {
  position: relative;
  grid-column: 2 / -1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(var(--mosaico-salas, 1), minmax(0, 1fr));
  column-gap: var(--mosaico-gap);
}

.mosaico-tv-trilha {
  container-type: inline-size;
  position: relative;
  min-width: 0;
  min-height: 0;
  border-radius: 0.8vh;
  border: 1px solid var(--mosaico-azul-medio);
  background-color: var(--mosaico-azul-escuro);
  /* Uma linha por hora: 10 faixas entre 8:00 e 18:00 */
  background-image: repeating-linear-gradient(to bottom,
      transparent 0,
      transparent calc(100% / 10 - 1px),
      var(--mosaico-azul-medio) calc(100% / 10 - 1px),
      var(--mosaico-azul-medio) calc(100% / 10));
}

.mosaico-tv-trilha-aviso {
  position: absolute;
  inset: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6cqw;
  text-align: center;
  font-size: clamp(0.7rem, 5.4cqw, 1.1rem);
  font-weight: 600;
  color: var(--mosaico-cinza);
}

.mosaico-tv-bloco {
  position: absolute;
  z-index: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.3vh;
  min-height: 0;
  overflow: hidden;
  padding: 0.6vh 4cqw;
  border-radius: 0.6vh;
  background: #1c4182;
  border: 1px solid rgba(55, 121, 227, 0.45);
  border-left: 0.35vw solid var(--mosaico-azul);
  line-height: 1.2;
}

.mosaico-tv-bloco--agora {
  z-index: 2;
  background: #543654;
  border-color: rgba(229, 83, 75, 0.55);
  border-left-color: var(--mosaico-ocupada);
  box-shadow: 0 0 14px rgba(229, 83, 75, 0.35);
}

.mosaico-tv-bloco--passado {
  opacity: 0.4;
}

.mosaico-tv-bloco-linha {
  display: flex;
  align-items: center;
  gap: 3cqw;
  min-width: 0;
}

.mosaico-tv-bloco-pessoas {
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
  padding: 0.05em 0.45em;
  border-radius: 999px;
  font-size: clamp(0.6rem, 4.8cqw, 0.9rem);
  font-weight: 700;
  color: var(--mosaico-branco);
  background: rgba(253, 253, 253, 0.14);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.mosaico-tv-bloco-pessoas i {
  font-size: 0.8em;
}

.mosaico-tv-bloco-hora {
  font-family: "Roboto", "Montserrat", Arial, sans-serif;
  font-size: clamp(0.62rem, 5cqw, 0.95rem);
  font-weight: 700;
  color: var(--mosaico-texto-suave);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.mosaico-tv-bloco-org {
  min-width: 0;
  font-size: clamp(0.7rem, 6cqw, 1.15rem);
  font-weight: 700;
  color: var(--mosaico-branco);
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Reuniões curtas: hora e nome na mesma linha */
.mosaico-tv-bloco--compacto {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  gap: 0 3cqw;
  padding-top: 0.3vh;
  padding-bottom: 0.3vh;
}

.mosaico-tv-bloco--compacto .mosaico-tv-bloco-org {
  flex: 1 1 0;
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.mosaico-tv-bloco--micro .mosaico-tv-bloco-hora {
  display: none;
}
/* Linha do horário atual atravessando todas as salas */
.mosaico-tv-agora-linha {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 5;
  height: 3px;
  pointer-events: none;
  transform: translateY(-50%);
  border-radius: 2px;
  background: var(--mosaico-agora);
  box-shadow: 0 0 10px rgba(255, 59, 48, 0.6);
}

.mosaico-tv-agora-linha::before {
  content: "";
  position: absolute;
  left: -0.5vh;
  top: 50%;
  width: 1.2vh;
  height: 1.2vh;
  border-radius: 50%;
  transform: translateY(-50%);
  background: var(--mosaico-agora);
}
</style>
